/**
 * A logged ticket on the day view (v0.9.0's P2b; the open-tickets roadmap):
 * whether it's open or closed decides its card's column, chip and colour.
 * "Logged shouldn't be the final form of a ticket, closed is" (the owner,
 * 2026-10-20). Pure functions of the API's ticket, so the cards, the popup
 * and the ribbon say the same.
 *
 * The states, as the owner-approved mockup shows them (its `stateRow`):
 * - **Open:** an amber "Open" chip, its Autotask status, and "open 3 d" or
 *   "opened today".
 * - **Closing…:** a blue chip, "Setting it to Complete".
 * - **Couldn't close:** an amber chip, "Try again".
 * - **Closed:** a green chip, "Closed by Axon Time", or "Closed in Autotask by
 *   Casey Sample".
 * - **Status unknown:** a neutral chip (ADR 0181: the open-tickets read failing
 *   for 30 minutes or more, or Autotask not finding it).
 */
import type { EntryTicket, LoggedTicket, TicketState } from "../../api/types";
import type { BadgeTone } from "../../components/ui/Badge";
import { formatShortDay, formatTimeIn, todayIn } from "../../lib/dates";

/** The board's third column: the owner's own words (2026-10-21; ADR 0183). */
export const OPEN_COLUMN = "Logged - Open";

export interface TicketView {
  ticketId: number;
  /** As techs know it (T20261002.0018); null when it isn't known. */
  number: string | null;
  state: TicketState;
  /** Its Autotask status's name as last read ("In Progress"). */
  statusLabel: string | null;
  /** When it was last known open. */
  openAsOf: string | null;
  /** When its age counts from: its first logging, or its making in Autotask. */
  ageFrom: string | null;
  closedAt: string | null;
  closedBy: "axon_time" | "autotask" | null;
  /** The tech who completed it, when one is mapped to them. */
  closedByName: string | null;
  /** Its latest close from Axon Time, while it's closing or that close failed. */
  closeActionId: string | null;
  /** When Axon Time last reopened it, and who asked, while it's open (P9; ADR 0197). */
  reopenedAt: string | null;
  reopenedByName: string | null;
}

export function ticketView(ticket: LoggedTicket | EntryTicket): TicketView {
  return {
    ticketId: ticket.ticket_id,
    number: ticket.number,
    state: ticket.state,
    statusLabel: ticket.status_label,
    openAsOf: ticket.open_as_of,
    ageFrom: ticket.age_from,
    closedAt: ticket.closed_at,
    closedBy: ticket.closed_by,
    closedByName: ticket.closed_by_name,
    closeActionId: ticket.close_action_id,
    reopenedAt: ticket.reopened_at ?? null,
    reopenedByName: ticket.reopened_by_name ?? null,
  };
}

/** How a ticket is named in words: its number, or its ID until that's known. */
export function ticketName(ticket: Pick<TicketView, "number" | "ticketId">): string {
  return ticket.number ?? String(ticket.ticketId);
}

/**
 * Where a logged ticket's card sits: Closed once it's closed; every other
 * state in Logged - Open, closing and a close that failed included, and a
 * status not known (with its "Status unknown" chip).
 */
export function ticketColumn(state: TicketState): "open" | "closed" {
  return state === "closed" ? "closed" : "open";
}

/** A logged block's status, in words: its card's heading, the capsule's name. */
export const STATE_LABELS: Record<TicketState, string> = {
  open: OPEN_COLUMN,
  closing: "Closing",
  close_failed: "Couldn't close",
  closed: "Closed",
  unknown: "Status unknown",
  reopening: "Reopening",
};

/** Each state's chip: the app's badge tones, never colour alone (section 11.0). */
export const STATE_CHIPS: Record<TicketState, { tone: BadgeTone; label: string }> = {
  open: { tone: "needs-client", label: "Open" },
  closing: { tone: "in-progress", label: "Closing…" },
  close_failed: { tone: "needs-client", label: "Couldn't close" },
  closed: { tone: "logged", label: "Closed" },
  unknown: { tone: "neutral", label: "Status unknown" },
  reopening: { tone: "in-progress", label: "Reopening…" },
};

/** Whether it can be closed from here now: open, or a close that failed (ADR 0182). */
export function canClose(state: TicketState): boolean {
  return state === "open" || state === "close_failed";
}

/** Whole days from one calendar day to another. */
function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000);
}

/** "today" or "3 d": how long ago it started, in the tech's own days. */
export function ageOf(since: string, now: Date, timeZone: string): string {
  const days = daysBetween(todayIn(timeZone, new Date(since)), todayIn(timeZone, now));
  return days <= 0 ? "today" : `${String(days)} d`;
}

/** "opened today" or "open 3 d", from when its age counts. */
export function openFor(since: string, now: Date, timeZone: string): string {
  const age = ageOf(since, now, timeZone);
  return age === "today" ? "opened today" : `open ${age}`;
}

/** "just now", "4 min ago", "2 h ago" or "3 d ago". */
export function agoText(at: string, now: Date): string {
  const minutes = Math.floor((now.getTime() - Date.parse(at)) / 60_000);
  if (minutes < 1) {
    return "just now";
  }
  if (minutes < 60) {
    return `${String(minutes)} min ago`;
  }
  const hours = Math.floor(minutes / 60);
  return hours < 24 ? `${String(hours)} h ago` : `${String(Math.floor(hours / 24))} d ago`;
}

/** "Closed by Axon Time", or "Closed in Autotask by Casey Sample" (or without a name, when none is mapped). */
export function closedByText(ticket: Pick<TicketView, "closedBy" | "closedByName">): string {
  if (ticket.closedBy === "autotask") {
    return ticket.closedByName ? `Closed in Autotask by ${ticket.closedByName}` : "Closed in Autotask";
  }
  return "Closed by Axon Time";
}

/**
 * What a card says beside its chip (the mockup's `stateRow`), and whether it
 * asks for something: "Try again", after a close that failed.
 */
export function stateText(ticket: TicketView, now: Date, timeZone: string): { text: string | null; warn: boolean } {
  switch (ticket.state) {
    case "open": {
      // Reopened by Axon Time (P9; ADR 0197): who asked, and when, in place of its age.
      const age =
        ticket.reopenedAt !== null
          ? reopenedText(ticket.reopenedAt, ticket.reopenedByName, now, timeZone)
          : ticket.ageFrom === null
            ? null
            : openFor(ticket.ageFrom, now, timeZone);
      const parts = [ticket.statusLabel, age];
      const text = parts.filter((part): part is string => part !== null).join(" · ");
      return { text: text === "" ? null : text, warn: false };
    }
    case "closing":
      return { text: "Setting it to Complete", warn: false };
    case "reopening":
      // A reopen to log to it (P9; ADR 0197): seconds, then it's open.
      return { text: "Setting it to In Progress", warn: false };
    case "close_failed":
      return { text: "Try again", warn: true };
    case "closed":
      return { text: closedByText(ticket), warn: false };
    case "unknown":
      return { text: "Autotask couldn't be read", warn: false };
  }
}

/** "reopened by Casey Sample today, 1:44 PM", or "reopened on Tue, Oct 20, 1:44 PM" without a name. */
export function reopenedText(at: string, by: string | null, now: Date, timeZone: string): string {
  const day = todayIn(timeZone, new Date(at));
  const time = formatTimeIn(at, timeZone);
  const when = day === todayIn(timeZone, now) ? `today, ${time}` : `on ${formatShortDay(day)}, ${time}`;
  return by ? `reopened by ${by} ${when}` : `reopened ${when}`;
}

/** "1:14 PM" today, or "Oct 20, 1:14 PM" another day: when, in the tech's own time. */
export function whenText(at: string, now: Date, timeZone: string): string {
  const day = todayIn(timeZone, new Date(at));
  const time = formatTimeIn(at, timeZone);
  return day === todayIn(timeZone, now) ? time : `${formatShortDay(day)}, ${time}`;
}

/**
 * The popup's line under "Logged to Autotask" (the mockup's `loggedHTML`):
 * "In Progress in Autotask · open 2 d · confirmed open 4 min ago" while it's
 * open; who closed it, and when, once it's closed.
 */
export function ticketLine(ticket: TicketView, now: Date, timeZone: string): string | null {
  if (canClose(ticket.state)) {
    return [
      ticket.statusLabel === null ? null : `${ticket.statusLabel} in Autotask`,
      ticket.ageFrom === null ? null : openFor(ticket.ageFrom, now, timeZone),
      ticket.openAsOf === null ? null : `confirmed open ${agoText(ticket.openAsOf, now)}`,
    ]
      .filter((part): part is string => part !== null)
      .join(" · ") || null;
  }
  if (ticket.state === "closed") {
    return ticket.closedAt === null
      ? closedByText(ticket)
      : `${closedByText(ticket)}, ${whenText(ticket.closedAt, now, timeZone)}`;
  }
  return null;
}

/** "open", "closed", …: how "Logged to Autotask: ticket T… · open" ends. */
export const LINE_STATES: Record<TicketState, string> = {
  open: "open",
  closing: "closing",
  close_failed: "open",
  closed: "closed",
  unknown: "status unknown",
  reopening: "reopening",
};

/**
 * The ribbon capsule of a logged block (the mockup's `capClass`): the logged
 * green now means closed; an open ticket's capsule has an amber outline on
 * the card's own surface; closing is blue, and a close that failed amber.
 */
export const CAPSULE_TONES: Record<TicketState, string> = {
  open: "border-[1.5px] border-status-needs-client-line bg-card text-status-needs-client-fg",
  unknown: "border-[1.5px] border-status-needs-client-line bg-card text-status-needs-client-fg",
  closing: "border-status-in-progress-line bg-status-in-progress-bg text-status-in-progress-fg",
  close_failed: "border-status-needs-client-line bg-status-needs-client-bg text-status-needs-client-fg",
  closed: "border-status-logged-bg bg-status-logged-bg text-status-logged-fg",
  // Being reopened (P9): blue, as a close under way is.
  reopening: "border-status-in-progress-line bg-status-in-progress-bg text-status-in-progress-fg",
};
